import React, { useState, useEffect, useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { useNavigate } from "react-router-dom";
import { Button, Input, ActionButton } from './common';
import { toast } from 'react-toastify';
import BrandDataService from "../services/BrandService";
import * as Yup from 'yup';
import { useFormik } from 'formik';
import log from "../services/logger";

const BrandsList = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalFormBrandVisible, setModalFormBrandVisible] = useState(false);
    const [editingBrand, setEditingBrand] = useState(null);
    const [searchName, setSearchName] = useState("");
    const navigate = useNavigate();
    const [pageSize, setPageSize] = useState(10);

    const retrieveBrands = async () => {
        try {
            setLoading(true);
            const response = await BrandDataService.getAll();
            setBrands(response.data);
        } catch (error) {
            toast.error('Greška pri učitavanju brendova');
            log.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        retrieveBrands();
    }, []);

    const handleSearch = async () => {
        try {
            setLoading(true);
            const response = await BrandDataService.findByName(searchName);
            setBrands(response.data);
        } catch (error) {
            toast.error('Greška pri pretraživanju');
            log.error(error);
        } finally {
            setLoading(false);
        }
    };

    const brandFormik = useFormik({
        initialValues: {
            brandName: editingBrand ? editingBrand.brandName : '',
        },
        validationSchema: Yup.object().shape({
            brandName: Yup.string().required('Morate uneti naziv brenda.'),
        }),
        enableReinitialize: true,
        onSubmit: async (values, { resetForm }) => {
            try {
                if (editingBrand) {
                    await BrandDataService.update(editingBrand.brand_id, values);
                    toast.success('Brend uspešno izmenjen!');
                }
                await retrieveBrands();
                setModalFormBrandVisible(false);
                resetForm();
                setEditingBrand(null);
            } catch (error) {
                toast.error('Došlo je do greške.');
                log.error(error);
            }
        },
    });

    const columns = useMemo(() => [
        {
            Header: "Ime Brenda",
            accessor: "brandName",
            sortType: 'alphanumeric',
        },
        {
            Header: "Akcije",
            id: 'actions',
            Cell: ({ row }) => (
                <div className="flex space-x-2">
                    <ActionButton
                        onClick={() => {
                            setEditingBrand(row.original);
                            setModalFormBrandVisible(true);
                        }}
                        icon="fa-edit"
                        label="Izmeni brend"
                        variant="primary"
                    />
                </div>
            ),
        },
    ], []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize: setTablePageSize,
        state: { pageIndex },
    } = useTable(
        {
            columns,
            data: brands,
            initialState: { pageSize },
        },
        useSortBy,
        usePagination
    );

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <i className="fas fa-tags text-blue-500 mr-3" />
                    Brendovi
                </h2>
                <p className="mt-2 text-gray-600">Upravljajte brendovima i njihovim informacijama</p>
            </div>

            {/* Search Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <Input
                        placeholder="Pretraži po imenu brenda"
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        className="flex-1"
                    />
                    <Button
                        onClick={handleSearch}
                        variant="primary"
                        className="whitespace-nowrap"
                    >
                        <i className="fas fa-search mr-2" />
                        Pretraži
                    </Button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                {loading ? (
                    <div className="flex justify-center items-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    {headerGroups.map(headerGroup => (
                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map(column => (
                                                <th
                                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                                >
                                                    <div className="flex items-center">
                                                        {column.render('Header')}
                                                        <span className="ml-2">
                                                            {column.isSorted ? (column.isSortedDesc ? '↓' : '↑') : ''}
                                                        </span>
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                                    {page.map(row => {
                                        prepareRow(row);
                                        return (
                                            <tr {...row.getRowProps()} className="hover:bg-gray-50">
                                                {row.cells.map(cell => (
                                                    <td
                                                        {...cell.getCellProps()}
                                                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                                    >
                                                        {cell.render('Cell')}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <Button
                                    onClick={() => previousPage()}
                                    disabled={!canPreviousPage}
                                    variant="secondary"
                                >
                                    Prethodna
                                </Button>
                                <Button
                                    onClick={() => nextPage()}
                                    disabled={!canNextPage}
                                    variant="secondary"
                                >
                                    Sledeća
                                </Button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Prikazano {pageIndex * pageSize + 1} - {Math.min((pageIndex + 1) * pageSize, brands.length)} od {brands.length}
                                    </p>
                                </div>
                                <div className="flex space-x-2">
                                    <select
                                        value={pageSize}
                                        onChange={e => {
                                            setPageSize(Number(e.target.value));
                                            setTablePageSize(Number(e.target.value));
                                        }}
                                        className="form-select rounded-md border-gray-300"
                                    >
                                        {[10, 20, 30, 40, 50].map(size => (
                                            <option key={size} value={size}>
                                                Prikaži {size}
                                            </option>
                                        ))}
                                    </select>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                        <Button
                                            onClick={() => previousPage()}
                                            disabled={!canPreviousPage}
                                            variant="secondary"
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                        >
                                            <i className="fas fa-chevron-left" />
                                        </Button>
                                        <Button
                                            onClick={() => nextPage()}
                                            disabled={!canNextPage}
                                            variant="secondary"
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                                        >
                                            <i className="fas fa-chevron-right" />
                                        </Button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Add Brand Button */}
            <div className="mt-6 flex justify-end">
                <Button
                    onClick={() => navigate('/brands/add')}
                    variant="primary"
                    className="inline-flex items-center"
                >
                    <i className="fas fa-plus mr-2" />
                    Dodaj Brend
                </Button>
            </div>

            {/* Edit Brand Modal */}
            {modalFormBrandVisible && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Izmena Brenda</h3>
                            <button
                                onClick={() => {
                                    setModalFormBrandVisible(false);
                                    setEditingBrand(null);
                                }}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <i className="fas fa-times" />
                            </button>
                        </div>
                        
                        <form onSubmit={brandFormik.handleSubmit} className="space-y-4">
                            <Input
                                label="Naziv Brenda"
                                name="brandName"
                                value={brandFormik.values.brandName}
                                onChange={brandFormik.handleChange}
                                onBlur={brandFormik.handleBlur}
                                error={brandFormik.touched.brandName && brandFormik.errors.brandName}
                            />
                            
                            <div className="flex justify-end space-x-3">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        setModalFormBrandVisible(false);
                                        setEditingBrand(null);
                                    }}
                                >
                                    Odustani
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                >
                                    Sačuvaj
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BrandsList;
